import { h } from 'preact';
import { useEffect, useState, useMemo } from 'preact/hooks';
import { memo } from 'preact/compat';
import { OrthomolIcon } from '../../components-common/OrthomolIcon';
import { Cart } from './Cart';
import { CartDrawerTotal } from './CartDrawerTotal';

// anti flicker
const MemoizedCart = memo(Cart);

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const animationDuration = 300;

export const CartDrawer = ({ isOpen, onClose }: CartDrawerProps) => {
  const [animationState, setAnimationState] = useState<'closed' | 'opening' | 'showing' | 'show' | 'hiding'>('closed');

  // animation state transitions + body scroll
  useEffect(() => {
    if (isOpen && animationState === 'closed') {
      document.body.style.overflow = 'hidden';
      setAnimationState('opening');
    } else if (!isOpen && animationState !== 'closed') {
      document.body.style.overflow = '';
      setAnimationState('hiding');

      const hideTimer = setTimeout(() => {
        setAnimationState('closed');
      }, animationDuration);

      return () => clearTimeout(hideTimer);
    }
  }, [isOpen, animationState]);

  // animation state progressions
  useEffect(() => {
    if (animationState === 'opening') {
      let raf2: number;
      const raf1 = requestAnimationFrame(() => {
        // wait for dom
        raf2 = requestAnimationFrame(() => {
          // wait for paint
          setAnimationState('showing'); // trigger slide-in
        });
      });

      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    } else if (animationState === 'showing') {
      // wait for css transition
      const showTimer = setTimeout(() => {
        setAnimationState('show');
      }, animationDuration);

      return () => clearTimeout(showTimer);
    }
  }, [animationState]);

  // clear body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const classes = ['slidein-shopping-cart', 'offcanvas'];

  // Bootstrap Classes: Drawer
  if (animationState === 'showing') classes.push('showing');
  if (animationState === 'show') classes.push('show');
  if (animationState === 'hiding') classes.push('hiding', 'show');

  // Bootstrap Classes: Backdrop
  const backdropClasses = ['offcanvas-backdrop', 'fade'];
  if (animationState === 'showing' || animationState === 'show') {
    backdropClasses.push('show');
  }

  const drawerHeader = useMemo(
    // anti flicker
    () => (
      <div className="slidein-shopping-cart-head offcanvas-header bg-gray-200 p-3 p-md-4 p-lg-3 mx-n3 mt-n3 mx-md-n4 mb-3">
        <h4 className="offcanvas-title text-primary fw-bold" id="slidein-shopping-cart-title">
          Zum Warenkorb hinzugefügt
        </h4>
        <button
          aria-label="Warenkorb schließen"
          onClick={onClose}
          className="btn text-primary p-0 border-0 bg-transparent"
        >
          <OrthomolIcon name="cross-24" />
        </button>
      </div>
    ),
    [onClose]
  );

  const shouldRender = animationState !== 'closed';

  return (
    <>
      {shouldRender && <div className={backdropClasses.join(' ')} onClick={onClose} />}
      {shouldRender && (
        <aside
          tabIndex={-1}
          id="slidein-shopping-cart"
          className={classes.join(' ')}
          aria-labelledby="slidein-shopping-cart-title"
        >
          <div className="offcanvas-body px-3 px-md-4 pt-3">
            {drawerHeader}
            <MemoizedCart onClose={onClose} />
          </div>
          <div className="d-block d-md-none d-lg-block">
            <CartDrawerTotal onClose={onClose} />
          </div>
        </aside>
      )}
    </>
  );
};
